import { TourProps } from 'antd';
import { useGetDataQuery } from 'common/commonRedux/getRTKApi/baseApi';
import { getCookie } from 'common/utility/_cookie';
import { RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { setHostCookie } from './tourCookie';
import { getTourContent } from './tourGuideApi';

export type TourGuideStatus = {
  accountId: number;
  isEnabled: boolean;
  canManage: boolean;
  updatedAt: string | null;
};

type BackendTourStep = { target?: string; title: string; description: string };

export const useTourGuideStatus = (skip = false) => {
  const { data, isLoading, refetch } = useGetDataQuery(
    { urlObjKey: 'TourGuideStatus', params: {} },
    { skip }
  );
  return { status: data as TourGuideStatus | undefined, isLoading, refetch };
};

/**
 * Generic driver for the backend-managed onboarding tour
 * (docs/TOUR_GUIDE_WEB_FRONTEND_GUIDE.md §4). Pass the page's Key 1 and a map
 * from the JSON content's `target` string to a mounted ref; the hook decides
 * whether to show anything (org switch + per-user/per-key1 cookie) and
 * returns ready-to-render antd `Tour` steps.
 */
export const useBackendTour = (
  key1: string,
  targetRefs: Record<string, RefObject<HTMLElement>>,
  skip = false
) => {
  const { i18n } = useTranslation();
  const { status, isLoading: statusLoading } = useTourGuideStatus(skip);
  const { profileData, originalTokenData } = useSelector((state: any) => state.auth, shallowEqual);
  const tokenData = originalTokenData ?? profileData?.tokenData;
  const userId = tokenData?.userid ?? tokenData?.employeeid;

  const [steps, setSteps] = useState<TourProps['steps']>([]);
  const [open, setOpen] = useState(false);

  const cookieName = userId ? `tf_tour_seen_${userId}_${key1}` : undefined;

  useEffect(() => {
    if (skip || statusLoading || !status?.isEnabled || !cookieName) return;
    if (getCookie(cookieName)) return;

    let cancelled = false;
    getTourContent(key1, i18n.language.split('-')[0])
      .then((entry) => {
        if (cancelled || !entry || entry.contentType !== 'json') return;
        const rawSteps: BackendTourStep[] = Array.isArray((entry.json as any)?.steps)
          ? (entry.json as any).steps
          : [];
        if (!rawSteps.length) return;

        const mapped: TourProps['steps'] = rawSteps.map((s) => {
          const ref = s.target ? targetRefs[s.target] : undefined;
          return {
            title: s.title,
            description: s.description,
            target: ref ? () => ref.current as HTMLElement : undefined,
          };
        });
        setSteps(mapped);
        setOpen(true);
      })
      .catch(() => {
        // Network / wrong-API-key failure: skip the backend-driven tour this load.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, statusLoading, status?.isEnabled, cookieName, key1, i18n.language]);

  const markSeen = () => {
    setOpen(false);
    if (cookieName) setHostCookie(cookieName, '1', 365);
  };

  return { open, steps, markSeen };
};
